import { useState } from 'react';
import type { Participant, ParticipantProgress } from '@filmsloth/shared';
import { useT } from '../i18n/index.js';

interface HostActions {
  onRemove: (participantId: string) => void;
  onMakeHost: (participantId: string) => void;
}

interface ParticipantListProps {
  participants: Participant[];
  meId: string;
  /** Present only for the host, who can manage everybody else in the room. */
  hostActions?: HostActions;
  /** How far each participant has got, keyed by id; shown while swiping. */
  progress?: Record<string, ParticipantProgress>;
}

function initials(name: string): string {
  return name.trim().slice(0, 2).toUpperCase();
}

export function ParticipantList({
  participants,
  meId,
  hostActions,
  progress,
}: ParticipantListProps) {
  const t = useT();
  const active = participants.filter((participant) => !participant.left);
  // Removing somebody is the one action here that cannot be taken back from
  // this screen, so it asks once more — per row, so the question sits next to
  // the name it is about.
  const [confirming, setConfirming] = useState<string | null>(null);

  return (
    <div className="list">
      {active.map((participant) => {
        const isMe = participant.id === meId;
        const voted = progress?.[participant.id];
        return (
          <div className="list__item participant" key={participant.id}>
            <span className={`avatar${participant.connected ? '' : ' avatar--offline'}`}>
              {initials(participant.name)}
            </span>
            <span className="participant__name">
              <span>
                {participant.name}
                {isMe ? <span className="muted small"> ({t('common.you')})</span> : null}
              </span>
              {voted ? (
                <span className="participant__progress small muted">
                  {voted.done
                    ? t('group.ratedDone', { count: voted.rated })
                    : t('group.rated', { count: voted.rated })}
                </span>
              ) : null}
            </span>
            <span className="spacer" />
            {participant.isHost ? <span className="badge">{t('common.host')}</span> : null}
            {hostActions && !isMe ? (
              confirming === participant.id ? (
                <span className="row participant__actions">
                  <button
                    type="button"
                    className="btn btn--small btn--danger"
                    onClick={() => {
                      setConfirming(null);
                      hostActions.onRemove(participant.id);
                    }}
                  >
                    {t('group.removeConfirm', { name: participant.name })}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() => setConfirming(null)}
                  >
                    {t('common.cancel')}
                  </button>
                </span>
              ) : (
                <span className="row participant__actions">
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() => hostActions.onMakeHost(participant.id)}
                  >
                    {t('group.makeHost')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    aria-label={t('group.removeLabel', { name: participant.name })}
                    onClick={() => setConfirming(participant.id)}
                  >
                    {t('group.remove')}
                  </button>
                </span>
              )
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
